import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User } from '@supabase/supabase-js';
import { authService } from '../services/auth';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { LoginModal } from '../components/LoginModal';
import './UseCases.css';

interface UseCase {
  id: string;
  title: string;
  category: string;
  excerpt: string;
  content: string[];
  capabilities: string[];
  examplePrompt: string;
  readTime: string;
}

// Category icons as SVG components
const CategoryIcon = ({ category }: { category: string }) => {
  const iconMap: Record<string, JSX.Element> = {
    'Product Development': (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
      </svg>
    ),
    'Mechanical Engineering': (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z"/>
      </svg>
    ),
    '3D Printing': (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/>
        <rect x="6" y="14" width="12" height="8"/>
      </svg>
    ),
    'Electronics': (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <rect x="4" y="4" width="16" height="16" rx="2"/>
        <rect x="9" y="9" width="6" height="6"/>
        <path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3"/>
      </svg>
    ),
    'Architecture': (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-6h6v6"/>
      </svg>
    ),
    'Manufacturing': (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
        <path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z"/>
      </svg>
    ),
  };
  return <div className="usecase-card-icon">{iconMap[category] || iconMap['Product Development']}</div>;
};

const useCases: UseCase[] = [
  {
    id: 'product-design',
    title: 'Accelerating Product Design from Concept to CAD',
    category: 'Product Development',
    excerpt: 'Transform product ideas into production-ready 3D models in seconds. Ideal for entrepreneurs, startups, and product teams who need to iterate quickly.',
    content: [
      'Traditional product design workflows require extensive CAD training and hours of manual modeling. Pinak fundamentally changes this equation by allowing designers to express intent through natural language.',
      'Whether you are prototyping a consumer electronics enclosure, designing custom furniture, or creating sporting goods, Pinak generates precise 3D geometry that meets manufacturing standards.',
      'The AI understands context. When you describe a "sleek wireless charger with ventilation," it applies appropriate design principles: smooth surfaces for aesthetics, optimized airflow patterns for thermal management, and manufacturing-conscious geometry for production.',
    ],
    capabilities: [
      'Generate complex product geometries from text descriptions',
      'Iterate on designs through conversational refinement',
      'Export manufacturing-ready STL, STEP, and IGES files',
      'Create multiple design variations for A/B testing',
    ],
    examplePrompt: 'Design a wireless charger with rounded corners and ventilation holes',
    readTime: '4 min read',
  },
  {
    id: 'mechanical-engineering',
    title: 'Precision Mechanical Parts for Engineering Teams',
    category: 'Mechanical Engineering',
    excerpt: 'Create engineering-grade mechanical components with exact specifications. From gears to brackets, Pinak handles the complexity.',
    content: [
      'Mechanical engineering demands precision. Every dimension, tolerance, and specification matters. Pinak was built with this understanding at its core.',
      'The system handles parametric relationships intelligently. Request a gear with specific module, tooth count, and bore diameter, and Pinak generates geometry that adheres to established engineering standards.',
      'Beyond individual components, Pinak understands assemblies. Mounting brackets account for bolt patterns. Shaft couplings consider alignment. Enclosures provide appropriate clearances for internal components.',
    ],
    capabilities: [
      'Parametric parts with precise dimensional control',
      'Standard mechanical components (gears, bearings, fasteners)',
      'Custom brackets, mounts, and structural elements',
      'Thread specifications and engineering tolerances',
    ],
    examplePrompt: 'Create a 20-tooth spur gear with module 2mm and 10mm bore hole',
    readTime: '5 min read',
  },
  {
    id: 'rapid-prototyping',
    title: 'Optimized Designs for Rapid Prototyping',
    category: '3D Printing',
    excerpt: 'Generate 3D-printable models optimized for FDM, SLA, or SLS printing with proper wall thickness, overhangs, and print orientation.',
    content: [
      'Not every 3D model is printable. Successful additive manufacturing requires geometry that accounts for printer capabilities, material properties, and post-processing requirements.',
      'Pinak generates print-optimized designs by default. Wall thicknesses meet minimum requirements. Overhangs stay within manageable angles. Features are oriented for optimal layer adhesion.',
      'For multi-part assemblies, Pinak considers clearances for assembly, snap-fit tolerances, and appropriate joint geometries. The result is designs that print successfully on the first attempt.',
    ],
    capabilities: [
      'Print-optimized geometry for all major technologies',
      'Automatic wall thickness validation',
      'Support structure recommendations',
      'Multi-part assembly designs with proper tolerances',
    ],
    examplePrompt: 'Design a phone stand with cable management, optimized for FDM printing',
    readTime: '4 min read',
  },
  {
    id: 'electronics-enclosures',
    title: 'Custom Enclosures for Electronics Projects',
    category: 'Electronics',
    excerpt: 'Design enclosures for Raspberry Pi, Arduino, or custom PCBs with ventilation, mounting points, and port cutouts.',
    content: [
      'Electronics enclosures require careful consideration of thermal management, EMI shielding, user interface access, and cable routing. Generic enclosures rarely fit specific needs.',
      'Pinak generates custom enclosures that account for exact board dimensions, component heights, connector positions, and mounting hole patterns. Standard boards like Raspberry Pi and Arduino are recognized automatically.',
      'Beyond basic housing, the system adds appropriate features: ventilation slots for heat dissipation, screw bosses for secure mounting, cable relief features for strain management, and access panels for maintenance.',
    ],
    capabilities: [
      'Standard board mountings (Raspberry Pi, Arduino, ESP32)',
      'Thermal management and ventilation design',
      'Port cutouts and cable routing solutions',
      'Snap-fit and screw boss features',
    ],
    examplePrompt: 'Create an enclosure for Raspberry Pi 4 with GPIO access and cooling vents',
    readTime: '5 min read',
  },
  {
    id: 'architectural-models',
    title: 'Architectural Visualization and Scale Models',
    category: 'Architecture',
    excerpt: 'Create architectural scale models, structural components, and building elements for visualization and client presentations.',
    content: [
      'Architectural visualization bridges the gap between concept and reality. Physical models communicate spatial relationships in ways that renders cannot match.',
      'Pinak generates architectural models at any scale, from detailed building components to site-wide masterplans. Structural elements follow proper proportions. Facade details maintain visual accuracy.',
      'For structural applications, the system understands load paths and connection details. Steel connections, concrete joints, and timber joinery follow established construction principles.',
    ],
    capabilities: [
      'Scale models at any specified ratio',
      'Structural components with proper detailing',
      'Custom building elements and facade systems',
      'Site model generation for context',
    ],
    examplePrompt: 'Design a modern pavilion structure with glass walls and cantilevered roof',
    readTime: '4 min read',
  },
  {
    id: 'manufacturing-tooling',
    title: 'Custom Tooling and Manufacturing Jigs',
    category: 'Manufacturing',
    excerpt: 'Design specialized fixtures, jigs, and tooling for production processes. From drill guides to assembly fixtures.',
    content: [
      'Manufacturing efficiency depends on proper tooling. Custom jigs reduce setup time, improve consistency, and enable complex operations that would otherwise require expensive equipment.',
      'Pinak generates purpose-built tooling: drill guides for precise hole placement, assembly fixtures for repeatable positioning, checking gauges for quality control, and holding fixtures for machining operations.',
      'The system understands manufacturing context. Drill bushings are sized for standard bits. Locating features provide appropriate clearances. Clamping surfaces are designed for available fixturing components.',
    ],
    capabilities: [
      'Assembly jigs and positioning fixtures',
      'Drill guides and machining templates',
      'Holding fixtures with clamping provisions',
      'Custom tool and part organizers',
    ],
    examplePrompt: 'Create a drill jig for 6 holes in a circle pattern, 50mm diameter',
    readTime: '4 min read',
  },
];

export const UseCases = () => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCase, setSelectedCase] = useState<UseCase | null>(null);

  useEffect(() => {
    const unsubscribe = authService.onAuthStateChange((currentUser) => {
      setUser(currentUser);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleSignOut = async () => {
    try {
      await authService.signOut();
      setUser(null);
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  if (isLoading) {
    return null;
  }

  return (
    <>
      <Header
        user={user}
        onLoginClick={() => setIsLoginModalOpen(true)}
        onSignOut={handleSignOut}
      />
      <LoginModal
        isOpen={isLoginModalOpen}
        onClose={() => setIsLoginModalOpen(false)}
        onLoginSuccess={() => {
          setIsLoginModalOpen(false);
        }}
      />

      <main className="page-main">
        {selectedCase ? (
          /* Article View */
          <section className="usecase-article-section">
            <div className="usecase-article-container">
              <button className="back-btn" onClick={() => setSelectedCase(null)}>
                <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M17 10a.75.75 0 01-.75.75H5.612l4.158 3.96a.75.75 0 11-1.04 1.08l-5.5-5.25a.75.75 0 010-1.08l5.5-5.25a.75.75 0 111.04 1.08L5.612 9.25H16.25A.75.75 0 0117 10z" clipRule="evenodd" />
                </svg>
                Back to all use cases
              </button>

              <article className="usecase-article">
                <div className="article-header">
                  <h1>{selectedCase.title}</h1>
                </div>
                
                <div className="article-body">
                  {selectedCase.content.map((paragraph, idx) => (
                    <p key={idx}>{paragraph}</p>
                  ))}
                </div>

                <div className="article-capabilities-box">
                  <h3>Key Capabilities</h3>
                  <ul>
                    {selectedCase.capabilities.map((cap, idx) => (
                      <li key={idx}>
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                        {cap}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="article-try-section">
                  <h3>Try It Yourself</h3>
                  <div className="example-prompt-card">
                    <code>{selectedCase.examplePrompt}</code>
                  </div>
                  <Link 
                    to={`/viewer?prompt=${encodeURIComponent(selectedCase.examplePrompt)}`}
                    className="btn-primary-lg"
                  >
                    Open in Designer
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M5 12h14M12 5l7 7-7 7"/>
                    </svg>
                  </Link>
                </div>
              </article>
            </div>
          </section>
        ) : (
          <>
            {/* Hero */}
            <section className="page-hero">
              <div className="page-hero-inner">
                <div className="page-badge">Use Cases</div>
                <h1>How Teams Use Pinak</h1>
                <p className="page-hero-description">
                  From rapid prototyping to production manufacturing, explore how organizations 
                  are transforming their design workflows with AI-powered CAD generation.
                </p>
              </div>
            </section>

            {/* Use Cases Grid */}
            <section className="usecases-grid-section">
              <div className="usecases-grid-container">
                <div className="usecases-grid-new">
                  {useCases.map((uc) => (
                    <article 
                      key={uc.id} 
                      className="usecase-card-new"
                      onClick={() => setSelectedCase(uc)}
                    >
                      <CategoryIcon category={uc.category} />
                      <div className="usecase-card-content">
                        <h2>{uc.title}</h2>
                        <p>{uc.excerpt}</p>
                        <span className="usecase-read-more">
                          Read more
                          <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
                          </svg>
                        </span>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            </section>

            {/* CTA Section */}
            <section className="page-cta-section">
              <div className="page-cta-container">
                <div className="page-cta-card">
                  <h2>Start Building Today</h2>
                  <p>
                    Join engineering teams and designers who are already using Pinak 
                    to accelerate their CAD workflows.
                  </p>
                  <div className="page-cta-buttons">
                    <Link to="/viewer" className="btn-primary-lg">
                      Open Designer
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M5 12h14M12 5l7 7-7 7"/>
                      </svg>
                    </Link>
                    <Link to="/pricing" className="btn-secondary-lg">View Pricing</Link>
                  </div>
                </div>
              </div>
            </section>
          </>
        )}
      </main>

      <Footer />
    </>
  );
};
